/**
 * A print run of labels, as a URL (PLAN-LABELS C1, D-LB5, D-LB9).
 *
 * `/batches/labels?beers=b-one,b-two&labels=b-one:1,2,3;b-two:4,5&recipe=true`.
 * The page at that address is both the sketch a brewer taps out and the paper
 * that prints (D-LB8), and this is the whole of its state: every tap is written
 * back here with `replace`, so a reload, a Back from anywhere, or the link
 * opened on the machine the label stock is loaded in all show the same sheet.
 *
 * A beer here is a batch or a blend (D-BS9). This file never tells them apart:
 * both kinds are minted from one document-id space, so a run is a list of
 * opaque ids and the page is what resolves each one against the batch list and
 * then the blend list. The `batchId` locals below are the older word for the
 * same thing.
 *
 * Two params rather than one, because a beer can be on the row without being
 * on the paper yet. `beers` is the chip row in the order the chips were added,
 * which is the order the tints go and so the order the sketch is coloured in;
 * `labels` is grouped by beer rather than listed cell by cell, with the
 * 1-based cell numbers a brewer counts on the paper, so the address bar reads
 * as the print run it is — which matters because that URL is the only durable
 * thing about a run and somebody will eventually read one.
 *
 * `sheet` is left out of the URL when it is the default, which is every URL
 * this app writes today: a param that can only ever say `avery-5520` is a wart
 * in the address bar, and the parser treats an absent sheet and an unknown one
 * the same way.
 *
 * ## Everything unrecognised is dropped, and nothing raises
 *
 * These values arrive from outside the app — a pasted link, a hand-edited
 * query, a bookmark from a version that numbered the cells differently — so an
 * id that is not a document id, a cell number the sheet does not have, and a
 * cell claimed twice are all handled by dropping the offending piece and
 * printing the rest. A hand-edited query should print the labels it did name
 * rather than an error page, exactly as the tasting cards' run does. In the
 * same spirit a beer that `labels` puts on the paper but `beers` forgot to
 * mention is appended to the chips rather than dropped: the paper is the
 * stronger claim of the two, and a sketch missing the chip for a beer it is
 * already showing would be a sheet the brewer could not edit. What this file
 * cannot check is whether the workspace actually *holds* a beer: that is a
 * question about documents, not about text, and the page answers it (G3).
 *
 * ## Both shapes of `labels` and `beers`
 *
 * A caller navigates here with the run as one string; the router then writes
 * what comes *out* of `validateSearch` back into the address bar, and a route
 * that returned the groups as an array would be handed an array on the very
 * next pass. The tasting cards route explains at length what that cost the
 * first time it was missed — a perfectly good run printing "we could not find
 * those beers" a moment after it opened — so both shapes are read here, and a
 * group is a group whether it arrived alone or beside its siblings.
 */

import { documentIdSchema } from '@beerwright/commands';
import {
  cellCount,
  DEFAULT_LABEL_SHEET,
  LABEL_SHEETS,
  type LabelSheetId,
  type LabelSheetSpec,
} from '@/lib/label-sheets';

/** Which beers are on the row, and which of them goes on which label. */
export interface LabelRun {
  readonly sheet: LabelSheetId;
  /**
   * The chip row, in the order the chips were added (D-LB9).
   *
   * Order is load-bearing rather than incidental: the tints are handed out
   * down this list, so a run that reordered it would repaint the sketch.
   */
  readonly beers: readonly string[];
  /** Cell number (1-based) to the beer's document id. Cells not in here stay blank. */
  readonly cells: ReadonlyMap<number, string>;
}

/** The search params a run occupies, in the shape the router serialises. */
export interface LabelRunSearch {
  /** The chip row, or `undefined` before a single beer is on it. */
  readonly beers: string | undefined;
  /**
   * The grouped run, or `undefined` for a run with nothing on it.
   *
   * `undefined` rather than `''` for the reason `lib/search-params.ts` gives:
   * the router stringifies every key a `validateSearch` returns, so an empty
   * string would leave a bare `?labels=` in the address bar of a page that is
   * already saying it has nothing to print.
   */
  readonly labels: string | undefined;
  /** Left out entirely when it is the default sheet. */
  readonly sheet: LabelSheetId | undefined;
  /** The tasting cards' opt-in, carried unchanged (D-LB5). */
  readonly recipe: true | undefined;
}

/** The sheet the search names, or the default for anything we do not know. */
export function labelRunSheet(value: unknown): LabelSheetSpec {
  if (typeof value !== 'string') return DEFAULT_LABEL_SHEET;
  return LABEL_SHEETS[value as LabelSheetId] ?? DEFAULT_LABEL_SHEET;
}

/**
 * The run, as the search params that carry it.
 *
 * Groups come out in the order their first cell appears on the sheet, and each
 * group's cells in ascending order, so two sketches that assigned the same
 * labels in a different order produce the same URL. The chips come out in
 * their own order instead, which is the one thing about a run that is not
 * derivable from the paper.
 */
export function labelRunSearch(run: LabelRun, recipe: boolean | undefined): LabelRunSearch {
  const groups = new Map<string, number[]>();
  for (const index of [...run.cells.keys()].sort((a, b) => a - b)) {
    const batchId = run.cells.get(index);
    if (batchId === undefined) continue;
    const held = groups.get(batchId);
    if (held === undefined) groups.set(batchId, [index]);
    else held.push(index);
  }
  const labels = [...groups.entries()]
    .map(([batchId, cells]) => `${batchId}:${cells.join(',')}`)
    .join(';');
  const beers = run.beers.join(',');
  return {
    beers: beers === '' ? undefined : beers,
    labels: labels === '' ? undefined : labels,
    sheet: run.sheet === DEFAULT_LABEL_SHEET.id ? undefined : run.sheet,
    recipe: recipe === true ? true : undefined,
  };
}

/** The run a search carries, with everything unrecognised dropped (D-LB5). */
export function parseLabelRun(search: {
  readonly beers?: unknown;
  readonly labels?: unknown;
  readonly sheet?: unknown;
}): LabelRun {
  const spec = labelRunSheet(search.sheet);
  const count = cellCount(spec);
  const cells = new Map<number, string>();
  for (const group of splitParam(search.labels, ';')) {
    const at = group.indexOf(':');
    if (at === -1) continue;
    const batchId = group.slice(0, at).trim();
    if (!documentIdSchema.safeParse(batchId).success) continue;
    for (const part of group.slice(at + 1).split(',')) {
      const index = Number(part.trim());
      if (!Number.isInteger(index) || index < 1 || index > count) continue;
      // First owner keeps the cell: a run that names label 4 twice is a
      // hand-edited URL, and the earlier group is the one that reads as the
      // brewer's intent.
      if (!cells.has(index)) cells.set(index, batchId);
    }
  }
  const beers: string[] = [];
  for (const batchId of splitParam(search.beers, ',')) {
    if (!documentIdSchema.safeParse(batchId).success) continue;
    if (!beers.includes(batchId)) beers.push(batchId);
  }
  // A beer on the paper is on the row whether the query said so or not. The
  // map is walked rather than the groups re-read because its insertion order
  // is already the order the groups arrived in.
  for (const batchId of cells.values()) {
    if (!beers.includes(batchId)) beers.push(batchId);
  }
  return { sheet: spec.id, beers, cells };
}

/**
 * One param as a flat list of parts, whichever shape it arrived in.
 *
 * `Number('')` is 0 rather than `NaN`, so an empty cell number would sail past
 * a bare `Number.isNaN` check and be dropped only by the range test above;
 * trimming here and the explicit range there are what actually reject
 * `b-one:,,` and `b-one: `.
 */
function splitParam(value: unknown, separator: string): readonly string[] {
  const parts =
    typeof value === 'string'
      ? [value]
      : Array.isArray(value)
        ? (value as unknown[]).filter((part): part is string => typeof part === 'string')
        : [];
  return parts
    .flatMap((part) => part.split(separator))
    .map((part) => part.trim())
    .filter((part) => part !== '');
}
